import { useState, useEffect, useCallback } from 'react'
import { useFiltersStore } from '@/store/filters'
import { biApi } from '@/lib/api'
import {
  Shuffle,
  RefreshCw,
  Calendar,
  Target,
  Zap,
  Award,
  Users,
  Package,
  Filter,
  Globe,
  Clock,
  Building2,
  Radio,
} from 'lucide-react'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from 'recharts'
import clsx from 'clsx'

// Chart colors - Orange-centered palette
const COLORS = [
  '#f97316', // Orange 500 (primary)
  '#fb923c', // Orange 400
  '#ea580c', // Orange 600
  '#fdba74', // Orange 300
  '#c2410c', // Orange 700
  '#22c55e', // Green (accent)
  '#3b82f6', // Blue (accent)
  '#8b5cf6', // Purple (accent)
]

interface RandomData {
  meta: {
    timezone: string
    startDate: string
    endDate: string
    granularity: string
    lastUpdated: string
    filtersApplied: Record<string, any>
    isAdmin: boolean
  }
  summary: {
    totalRandomTraffic: number
    totalConversions: number
    overallConversionRate: number
    productsInPool: number
    productsWithTraffic: number
    selectionMethods: {
      performanceWeighted: { count: number; percentage: number }
      randomInsufficient: { count: number; percentage: number }
    }
  }
  productRanking: Array<{
    productId: number
    productName: string
    reference: string
    customerName: string
    clicks: number
    conversions: number
    conversionRate: number
    trafficShare: number
    performanceScore: number
    performanceWeighted: number
    randomInsufficient: number
  }>
  timeSeries: Array<{
    date: string
    clicks: number
    conversions: number
    performanceWeighted: number
    randomInsufficient: number
  }>
  randomPool: Array<{
    productId: number
    name: string
    reference: string
    country: string
    operator: string
    customerName: string
    hasTraffic: boolean
  }>
}

// KPI Card component
function KPICard({
  title,
  value,
  icon: Icon,
  subtitle,
  color = 'primary',
}: {
  title: string
  value: string | number
  icon: React.FC<{ className?: string }>
  subtitle?: string
  color?: 'primary' | 'success' | 'warning' | 'info'
}) {
  const colorClasses = {
    primary: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600',
    success: 'bg-green-50 dark:bg-green-500/20 text-green-600',
    warning: 'bg-yellow-50 dark:bg-yellow-500/20 text-yellow-600',
    info: 'bg-blue-50 dark:bg-blue-500/20 text-blue-600',
  }
  
  return (
    <div className="kpi-card">
      <div className="flex items-start justify-between">
        <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        {subtitle && (
          <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  )
}

// Date presets
const DATE_PRESETS = [
  { label: 'Hoy', days: 0 },
  { label: '7 días', days: 7 },
  { label: '30 días', days: 30 },
  { label: '90 días', days: 90 },
]

export default function Random() {
  const { 
    filters, 
    options, 
    setOptions, 
    setFilter,
    setDateRange,
    setGranularity: setGranularityStore,
    optionsLoaded 
  } = useFiltersStore()
  
  // Destructure common filters from store
  const { 
    startDate, 
    endDate, 
    timezone: selectedTimezone, 
    granularity,
    countries: selectedCountries,
    operators: selectedOperators,
    customerIds: selectedCustomers,
    flow: selectedFlow
  } = filters
  
  const [data, setData] = useState<RandomData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activePreset, setActivePreset] = useState<number | null>(7)

  // Load filter options on mount
  useEffect(() => {
    const loadFilters = async () => {
      if (!optionsLoaded) {
        try {
          const response = await biApi.getFilterOptions()
          if (response.data.success) {
            setOptions(response.data.data)
          }
        } catch (err) {
          console.error('Error loading filter options:', err)
        }
      }
    }
    loadFilters()
  }, [optionsLoaded, setOptions])

  // Get filtered operators based on selected countries
  const filteredOperators = selectedCountries.length > 0
    ? options.operators.list.filter(op => selectedCountries.includes(op.country))
    : options.operators.list

  // Get filtered customers based on selected country and operator
  // Customers are determined by products that match the selected country/operator
  const filteredCustomers = (() => {
    let products = options.products || []
    
    // Filter by country if selected
    if (selectedCountries.length > 0) {
      products = products.filter(p => selectedCountries.includes(p.country))
    }
    
    // Filter by operator if selected
    if (selectedOperators.length > 0) {
      products = products.filter(p => selectedOperators.includes(p.operator))
    }
    
    // Get unique customer IDs from filtered products
    const customerIds = [...new Set(products.map(p => p.customerId).filter(Boolean))]
    
    // Return customers that match those IDs
    return options.customers.filter(c => customerIds.includes(c.id))
  })()

  // Check if filters are disabled
  const isOperatorDisabled = selectedCountries.length === 0
  const isCustomerDisabled = selectedOperators.length === 0

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await biApi.getRandomAnalysis({
        startDate,
        endDate,
        timezone: selectedTimezone,
        granularity,
        countries: selectedCountries.length > 0 ? selectedCountries : undefined,
        operators: selectedOperators.length > 0 ? selectedOperators : undefined,
        customerIds: selectedCustomers.length > 0 ? selectedCustomers : undefined,
        flow: selectedFlow || undefined,
      })
      
      setData(response.data.data)
    } catch (err: any) {
      console.error('Error fetching random data:', err)
      setError(err.response?.data?.error || 'Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, granularity, selectedTimezone, selectedCountries, selectedOperators, selectedCustomers, selectedFlow])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Handle preset click
  const handlePresetClick = (days: number) => {
    const end = new Date()
    const start = new Date()
    if (days > 0) {
      start.setDate(start.getDate() - days)
    }
    setDateRange(start.toISOString().split('T')[0], end.toISOString().split('T')[0])
    setActivePreset(days)
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
          <Shuffle className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Error al cargar datos
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
        <button onClick={fetchData} className="btn-primary">
          Reintentar
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Análisis de Random
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Distribución de tráfico y rendimiento por producto
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Filtros</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Date Presets & Range */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Rango de Fechas
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.days}
                  onClick={() => handlePresetClick(preset.days)}
                  className={clsx(
                    'px-3 py-1.5 text-sm rounded-lg transition-colors',
                    activePreset === preset.days
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  )}
                >
                  {preset.label}
                </button>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setDateRange(e.target.value, endDate)
                    setActivePreset(null)
                  }}
                  className="input text-sm"
                />
                <span className="text-gray-400">→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setDateRange(startDate, e.target.value)
                    setActivePreset(null)
                  }}
                  className="input text-sm"
                />
              </div>
            </div>
          </div>
          
          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Zona Horaria
            </label>
            <select
              value={selectedTimezone}
              onChange={(e) => setFilter('timezone', e.target.value)}
              className="input w-full"
            >
              {options.timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Granularity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Granularidad
            </label>
            <select
              value={granularity}
              onChange={(e) => setGranularityStore(e.target.value as 'hour' | 'day' | 'week')}
              className="input w-full"
            >
              <option value="hour">Por hora</option>
              <option value="day">Por día</option>
              <option value="week">Por semana</option>
            </select>
          </div>
          
          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Globe className="w-4 h-4 inline mr-1" />
              País
            </label>
            <select
              value={selectedCountries[0] || ''}
              onChange={(e) => {
                const val = e.target.value
                setFilter('countries', val ? [val] : [])
                setFilter('operators', []) // Reset operators when country changes
                setFilter('customerIds', []) // Reset customers when country changes
              }}
              className="input w-full"
            >
              <option value="">Todos los países</option>
              {options.countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Operator - depends on country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Radio className="w-4 h-4 inline mr-1" />
              Operador
            </label>
            <select
              value={selectedOperators[0] || ''}
              onChange={(e) => {
                const val = e.target.value
                setFilter('operators', val ? [val] : [])
                setFilter('customerIds', []) // Reset customers when operator changes
              }}
              className={clsx(
                "input w-full",
                isOperatorDisabled && "opacity-50 cursor-not-allowed"
              )}
              disabled={isOperatorDisabled}
            >
              <option value="">{isOperatorDisabled ? "Seleccione país primero" : "Todos los operadores"}</option>
              {filteredOperators.map((op) => (
                <option key={`${op.country}-${op.operator}`} value={op.operator}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Customer - depends on operator */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Cliente
            </label>
            <select
              value={selectedCustomers[0] || ''}
              onChange={(e) => {
                const val = e.target.value
                setFilter('customerIds', val ? [Number(val)] : [])
              }}
              className={clsx(
                "input w-full",
                isCustomerDisabled && "opacity-50 cursor-not-allowed"
              )}
              disabled={isCustomerDisabled}
            >
              <option value="">{isCustomerDisabled ? "Seleccione operador primero" : "Todos los clientes"}</option>
              {filteredCustomers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Flow filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Flujo
            </label>
            <select
              value={selectedFlow}
              onChange={(e) => setFilter('flow', e.target.value)}
              className="input w-full"
            >
              <option value="">Todos los flujos</option>
              <option value="sms">SMS</option>
              <option value="sat">SAT</option>
            </select>
          </div>
          
          {/* Apply Filters Button */}
          <div className="flex items-end">
            <button
              onClick={fetchData}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
              Aplicar Filtros
            </button>
          </div>
        </div>
        
        {/* Active filters indicator */}
        {(selectedCountries.length > 0 || selectedOperators.length > 0 || selectedCustomers.length > 0) && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500">Filtros activos:</span>
              {selectedCountries.map(c => {
                const country = options.countries.find(cc => cc.code === c)
                return (
                  <span key={c} className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded text-xs">
                    {country?.flag} {country?.name || c}
                  </span>
                )
              })}
              {selectedOperators.map(op => (
                <span key={op} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                  {op}
                </span>
              ))}
              {selectedCustomers.map(cid => {
                const customer = options.customers.find(c => c.id === cid)
                return (
                  <span key={cid} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                    {customer?.shortName || customer?.name || cid}
                  </span>
                )
              })}
              <button
                onClick={() => {
                  setFilter('countries', [])
                  setFilter('operators', [])
                  setFilter('customerIds', [])
                }}
                className="text-xs text-gray-500 hover:text-red-500 underline"
              >
                Limpiar filtros
              </button>
            </div>
          </div>
        )}
        
        {/* Timezone indicator */}
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          📍 Zona horaria: <strong>{options.timezones.find(t => t.value === selectedTimezone)?.label || selectedTimezone}</strong>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard
              title="Tráfico Random Total"
              value={data.summary.totalRandomTraffic}
              icon={Shuffle}
              color="primary"
            />
            <KPICard
              title="Conversiones"
              value={data.summary.totalConversions}
              icon={Target}
              subtitle={`${data.summary.overallConversionRate.toFixed(2)}% rate`}
              color="success"
            />
            <KPICard
              title="Performance-Weighted"
              value={`${data.summary.selectionMethods.performanceWeighted.percentage.toFixed(1)}%`}
              icon={Zap}
              subtitle={`${data.summary.selectionMethods.performanceWeighted.count} selecciones`}
              color="warning"
            />
            <KPICard
              title="Productos en Pool"
              value={data.summary.productsInPool}
              icon={Package}
              subtitle={`${data.summary.productsWithTraffic} con tráfico`}
              color="info"
            />
          </div>

          {/* Selection Method Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Method Distribution Pie */}
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Método de Selección
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      <linearGradient id="methodGradient1" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#ff9500" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#f97316" stopOpacity={1}/>
                      </linearGradient>
                      <linearGradient id="methodGradient2" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={1}/>
                      </linearGradient>
                    </defs>
                    <Pie
                      data={[
                        { name: 'Performance-Weighted', value: data.summary.selectionMethods.performanceWeighted.count },
                        { name: 'Random (datos insuf.)', value: data.summary.selectionMethods.randomInsufficient.count },
                      ]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      labelLine={{ stroke: '#9ca3af' }}
                    >
                      <Cell fill="url(#methodGradient1)" style={{ filter: 'drop-shadow(0 2px 4px rgba(249, 115, 22, 0.3))' }} />
                      <Cell fill="url(#methodGradient2)" style={{ filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))' }} />
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Traffic Share by Product */}
            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Distribución de Tráfico por Producto
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {data.productRanking.slice(0, 6).map((_, index) => (
                        <linearGradient key={index} id={`productGradient${index}`} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={COLORS[index]} stopOpacity={1}/>
                          <stop offset="100%" stopColor={COLORS[index]} stopOpacity={0.7}/>
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={data.productRanking.slice(0, 6).map(p => ({
                        name: p.reference || p.productName,
                        value: p.clicks,
                        rate: p.conversionRate
                      }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                      labelLine={{ stroke: '#9ca3af' }}
                    >
                      {data.productRanking.slice(0, 6).map((_, index) => (
                        <Cell 
                          key={index} 
                          fill={`url(#productGradient${index})`}
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(249, 115, 22, 0.2))' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value: number, name: string, props: any) => [
                        `${value.toLocaleString()} clicks (${props.payload.rate.toFixed(2)}% conv)`,
                        name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Time Series - Selection Method Over Time */}
          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Método de Selección en el Tiempo
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.timeSeries}>
                  <defs>
                    <linearGradient id="colorPerformance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorRandom" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickFormatter={(value) => {
                      if (granularity === 'hour') return value.split(' ')[1] || value
                      return value.split('-').slice(1).join('/')
                    }}
                  />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="performanceWeighted"
                    name="Performance-Weighted"
                    stroke="#f97316"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorPerformance)"
                    stackId="1"
                  />
                  <Area
                    type="monotone"
                    dataKey="randomInsufficient"
                    name="Random (datos insuf.)"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRandom)"
                    stackId="1"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Product Ranking Table */}
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <Award className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Ranking de Productos por Performance
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referencia
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clicks
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conv.
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Traffic Share
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance Score
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {data.productRanking.map((product, index) => (
                    <tr key={product.productId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={clsx(
                          'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold',
                          index === 0 && 'bg-yellow-100 text-yellow-800',
                          index === 1 && 'bg-gray-100 text-gray-800',
                          index === 2 && 'bg-orange-100 text-orange-800',
                          index > 2 && 'bg-gray-50 text-gray-600'
                        )}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {product.productName}
                          </p>
                          <p className="text-xs text-gray-500">{product.customerName}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {product.reference || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                        {product.clicks.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                        {product.conversions.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <span className={clsx(
                          'text-sm font-medium',
                          product.conversionRate >= 5 && 'text-green-600',
                          product.conversionRate >= 2 && product.conversionRate < 5 && 'text-yellow-600',
                          product.conversionRate < 2 && 'text-red-600'
                        )}>
                          {product.conversionRate.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full"
                              style={{ width: `${Math.min(product.trafficShare, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-right">
                            {product.trafficShare.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className={clsx(
                            'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold',
                            product.performanceScore >= 0.8 && 'bg-green-100 text-green-800',
                            product.performanceScore >= 0.5 && product.performanceScore < 0.8 && 'bg-yellow-100 text-yellow-800',
                            product.performanceScore < 0.5 && 'bg-red-100 text-red-800'
                          )}>
                            {(product.performanceScore * 100).toFixed(0)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {data.productRanking.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                No hay datos de tráfico random en el período seleccionado
              </div>
            )}
          </div>

          {/* Random Pool */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Pool de Productos Random
              </h3>
              <span className="text-sm text-gray-500">
                ({data.randomPool.length} productos activos)
              </span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {data.randomPool.map((product) => (
                <div
                  key={product.productId}
                  className={clsx(
                    'p-3 rounded-lg border transition-colors',
                    product.hasTraffic
                      ? 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {product.reference || `ID: ${product.productId}`}
                      </p>
                    </div>
                    {product.hasTraffic && (
                      <span className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full ml-2 mt-1" title="Con tráfico" />
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <span>{product.country}</span>
                    <span>•</span>
                    <span>{product.operator}</span>
                  </div>
                </div>
              ))}
            </div>
            
            {data.randomPool.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No hay productos configurados para distribución random
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}
